import { useState, useEffect, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Loader2, MapPin, CalendarIcon, Compass, DollarSign, X, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { LocationAutocomplete, PlaceData } from "@/components/ui/location-autocomplete";
import { EquipmentSelect } from "@/components/ui/equipment-select";
import {
  DispatcherWatch,
  useDispatcherWatches,
} from "@/hooks/useDispatcherWatches";
import { validateWatchCriteria } from "@/lib/dispatcher/limits";


const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO",
  "MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const REGIONS: { key: string; label: string; states: string[] }[] = [
  { key: "northeast", label: "Northeast", states: ["NY","NJ","PA","CT","MA","RI","VT","NH","ME"] },
  { key: "southeast", label: "Southeast", states: ["FL","GA","SC","NC","VA","AL","TN","MS","LA"] },
  { key: "midwest", label: "Midwest", states: ["OH","IL","IN","MI","WI","MN","IA","MO","KS","NE","ND","SD"] },
  { key: "south_central", label: "South Central", states: ["TX","OK","AR"] },
  { key: "mountain_west", label: "Mountain West", states: ["CO","WY","MT","ID","UT","NV","AZ","NM"] },
  { key: "pacific", label: "Pacific", states: ["CA","OR","WA"] },
];

// Derive selected region keys + leftover custom states from a flat state array
function deriveRegionsFromStates(states: string[] | null | undefined): { regionKeys: string[]; customStates: string[] } {
  if (!states || states.length === 0) return { regionKeys: [], customStates: [] };
  const set = new Set(states);
  const regionKeys: string[] = [];
  for (const r of REGIONS) {
    if (r.states.every((s) => set.has(s))) {
      regionKeys.push(r.key);
      r.states.forEach((s) => set.delete(s));
    }
  }
  return { regionKeys, customStates: Array.from(set) };
}

function flattenRegions(regionKeys: string[], customStates: string[]): string[] {
  const out = new Set<string>(customStates);
  for (const key of regionKeys) {
    const r = REGIONS.find((x) => x.key === key);
    if (r) r.states.forEach((s) => out.add(s));
  }
  return Array.from(out);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: DispatcherWatch | null;
}

interface RegionPillsProps {
  selectedRegions: string[];
  customStates: string[];
  onToggleRegion: (key: string) => void;
  onClearAll: () => void;
  onAddCustomState: (state: string) => void;
  onRemoveCustomState: (state: string) => void;
  tone?: "teal" | "destructive";
  anywhereLabel?: string;
}

function RegionPills({
  selectedRegions, customStates, onToggleRegion, onClearAll,
  onAddCustomState, onRemoveCustomState, tone = "teal", anywhereLabel = "Anywhere",
}: RegionPillsProps) {
  const [customOpen, setCustomOpen] = useState(false);
  const isEmpty = selectedRegions.length === 0 && customStates.length === 0;

  const selectedClass = tone === "destructive"
    ? "bg-destructive/10 text-destructive border-destructive/40 hover:bg-destructive/15"
    : "bg-brand-teal text-white border-brand-teal hover:bg-brand-teal/90";

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={onClearAll}
          className={cn(
            "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
            isEmpty ? selectedClass : "bg-background border-border hover:bg-muted"
          )}
        >
          {anywhereLabel}
        </button>
        {REGIONS.map((r) => {
          const active = selectedRegions.includes(r.key);
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => onToggleRegion(r.key)}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                active ? selectedClass : "bg-background border-border hover:bg-muted"
              )}
            >
              {r.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setCustomOpen((v) => !v)}
          className={cn(
            "px-3 py-1 rounded-full text-xs font-medium border transition-colors inline-flex items-center gap-1",
            customStates.length > 0 ? selectedClass : "bg-background border-border hover:bg-muted"
          )}
        >
          <Plus className="h-3 w-3" />
          Custom{customStates.length > 0 ? ` (${customStates.length})` : ""}
        </button>
      </div>

      {customStates.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {customStates.map((s) => (
            <Badge key={s} variant="secondary" className="gap-1 pr-1">
              {s}
              <button
                type="button"
                onClick={() => onRemoveCustomState(s)}
                className="hover:bg-muted-foreground/20 rounded-full p-0.5"
                aria-label={`Remove ${s}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {customOpen && (
        <div className="rounded-md border bg-background p-2">
          <div className="text-xs text-muted-foreground mb-1.5">Select specific states</div>
          <div className="grid grid-cols-8 sm:grid-cols-10 gap-1 max-h-32 overflow-y-auto">
            {US_STATES.map((s) => {
              const checked = customStates.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => checked ? onRemoveCustomState(s) : onAddCustomState(s)}
                  className={cn(
                    "px-1.5 py-1 text-xs rounded border transition-colors",
                    checked
                      ? "bg-brand-teal text-white border-brand-teal"
                      : "bg-background border-border hover:bg-muted"
                  )}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionCard({
  icon: Icon, title, children,
}: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-brand-teal" />
        {title}
      </div>
      {children}
    </div>
  );
}

export function WatchLaneModal({ open, onOpenChange, editing }: Props) {
  const { createWatch, updateWatch } = useDispatcherWatches();
  const [saving, setSaving] = useState(false);

  const [label, setLabel] = useState("");
  const [locationLabel, setLocationLabel] = useState("");
  const [currentCity, setCurrentCity] = useState("");
  const [currentState, setCurrentState] = useState("");
  const [deadhead, setDeadhead] = useState(150);
  const [equipment, setEquipment] = useState<string[]>([]);
  const [pickupFrom, setPickupFrom] = useState<Date | undefined>();
  const [pickupTo, setPickupTo] = useState<Date | undefined>();
  const [minRate, setMinRate] = useState("");
  const [minRpm, setMinRpm] = useState("");
  const [maxWeight, setMaxWeight] = useState("");

  const [destRegions, setDestRegions] = useState<string[]>([]);
  const [destCustom, setDestCustom] = useState<string[]>([]);
  const [avoidRegions, setAvoidRegions] = useState<string[]>([]);
  const [avoidCustom, setAvoidCustom] = useState<string[]>([]);

  const [notifyEmail, setNotifyEmail] = useState(true);
  const [autoScore, setAutoScore] = useState(true);

  const [errors, setErrors] = useState<{ location?: string; equipment?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);


  useEffect(() => {
    if (editing) {
      setLabel(editing.label || "");
      setCurrentCity(editing.current_city || "");
      setCurrentState(editing.current_state || "");
      setLocationLabel(
        editing.current_city && editing.current_state
          ? `${editing.current_city}, ${editing.current_state}`
          : editing.current_state || ""
      );
      setDeadhead(editing.max_deadhead_miles);
      setEquipment(editing.equipment_types || []);
      setPickupFrom(editing.pickup_date_from ? parseISO(editing.pickup_date_from) : undefined);
      setPickupTo(editing.pickup_date_to ? parseISO(editing.pickup_date_to) : undefined);
      setMinRate(editing.min_rate?.toString() || "");
      setMinRpm(editing.min_rpm?.toString() || "");
      setMaxWeight(editing.max_weight_lbs?.toString() || "");

      const dest = deriveRegionsFromStates(editing.destination_states);
      setDestRegions(dest.regionKeys);
      setDestCustom(dest.customStates);
      const avoid = deriveRegionsFromStates(editing.avoid_states);
      setAvoidRegions(avoid.regionKeys);
      setAvoidCustom(avoid.customStates);

      setNotifyEmail(editing.notify_email);
      setAutoScore(editing.auto_score);
    } else {
      setLabel(""); setLocationLabel(""); setCurrentCity(""); setCurrentState("");
      setDeadhead(150); setEquipment([]);
      setPickupFrom(undefined); setPickupTo(undefined);
      setMinRate(""); setMinRpm(""); setMaxWeight("");
      setDestRegions([]); setDestCustom([]); setAvoidRegions([]); setAvoidCustom([]);
      setNotifyEmail(true); setAutoScore(true);
    }
    setErrors({});
    setSubmitError(null);

  }, [editing, open]);

  const handleLocationChange = (value: string, placeData?: PlaceData) => {
    setLocationLabel(value);
    if (placeData) {
      setCurrentCity(placeData.city);
      setCurrentState(placeData.state);
      setErrors((e) => ({ ...e, location: undefined }));
    } else if (!value) {
      setCurrentCity(""); setCurrentState("");
    }
  };

  const toggleRegion = (
    key: string,
    selected: string[],
    setSelected: (v: string[]) => void
  ) => {
    setSelected(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };

  const onSave = async () => {
    setSubmitError(null);
    const nextErrors: typeof errors = {};
    if (!currentState) nextErrors.location = "Pick your current city/state";
    if (equipment.length === 0) nextErrors.equipment = "Pick at least one equipment type";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error("Fix the highlighted fields");
      return;
    }

    const destStates = flattenRegions(destRegions, destCustom);
    const avoidStates = flattenRegions(avoidRegions, avoidCustom);

    // Strict criteria validation (Limit 6) — surface inline before hitting the network
    const criteria = validateWatchCriteria({
      current_city: currentCity.trim() || null,
      current_state: currentState || null,
      destination_states: destStates.length ? destStates : null,
      equipment_types: equipment,
      min_rate: minRate ? Number(minRate) : null,
      min_rpm: minRpm ? Number(minRpm) : null,
    });
    if (!criteria.ok) {
      setSubmitError((criteria as { ok: false; error: string }).error);
      return;
    }

    setSaving(true);
    try {
      const payload: Partial<DispatcherWatch> = {
        label: label.trim() || null,
        current_city: currentCity.trim() || null,
        current_state: currentState,
        max_deadhead_miles: deadhead,
        equipment_types: equipment,
        pickup_date_from: pickupFrom ? format(pickupFrom, "yyyy-MM-dd") : null,
        pickup_date_to: pickupTo ? format(pickupTo, "yyyy-MM-dd") : null,
        min_rate: minRate ? Number(minRate) : null,
        min_rpm: minRpm ? Number(minRpm) : null,
        destination_states: destStates.length ? destStates : null,
        max_weight_lbs: maxWeight ? Number(maxWeight) : null,
        avoid_states: avoidStates.length ? avoidStates : null,
        notify_email: notifyEmail,
        auto_score: autoScore,
      };
      if (editing) {
        await updateWatch(editing.id, payload);
        toast.success("Watch updated");
      } else {
        await createWatch(payload);
        toast.success("Watch created — ATLAS is monitoring");
      }
      onOpenChange(false);
    } catch (e: any) {
      const msg = e?.message || "Save failed";
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand-teal" />
            {editing ? "Edit Watch" : "Watch a Lane"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-muted-foreground">Watch name (optional)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Home base run"
              className="mt-1"
            />
          </div>

          <SectionCard icon={MapPin} title="Where I'm Running">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Current location *</Label>
                <div className="mt-1">
                  <LocationAutocomplete
                    value={locationLabel}
                    onChange={handleLocationChange}
                    placeholder="City, ST or ZIP"
                    error={errors.location}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Equipment *</Label>
                <div className="mt-1">
                  <EquipmentSelect
                    mode="multi"
                    value={equipment}
                    onChange={(v) => {
                      setEquipment(Array.isArray(v) ? v : [v]);
                      if ((Array.isArray(v) ? v.length : 1) > 0) {
                        setErrors((e) => ({ ...e, equipment: undefined }));
                      }
                    }}
                    placeholder="Select equipment"
                    error={!!errors.equipment}
                  />
                </div>
                {errors.equipment && (
                  <p className="text-xs text-destructive mt-1">{errors.equipment}</p>
                )}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Max deadhead</Label>
                <span className="text-xs font-semibold text-brand-teal">{deadhead} mi</span>
              </div>
              <Slider
                value={[deadhead]}
                onValueChange={([v]) => setDeadhead(v)}
                min={25} max={500} step={25}
                className="mt-2"
              />
            </div>
          </SectionCard>

          <SectionCard icon={CalendarIcon} title="When">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Pickup from</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full mt-1 justify-start text-left font-normal",
                        !pickupFrom && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 opacity-60" />
                      {pickupFrom ? format(pickupFrom, "MMM d, yyyy") : "Any"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={pickupFrom}
                      onSelect={setPickupFrom}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs">Pickup to</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full mt-1 justify-start text-left font-normal",
                        !pickupTo && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 opacity-60" />
                      {pickupTo ? format(pickupTo, "MMM d, yyyy") : "Any"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={pickupTo}
                      onSelect={setPickupTo}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={Compass} title="Destination Preferences">
            <div>
              <Label className="text-xs text-muted-foreground">Where you want to deliver</Label>
              <div className="mt-2">
                <RegionPills
                  selectedRegions={destRegions}
                  customStates={destCustom}
                  onToggleRegion={(k) => toggleRegion(k, destRegions, setDestRegions)}
                  onClearAll={() => { setDestRegions([]); setDestCustom([]); }}
                  onAddCustomState={(s) => setDestCustom((prev) => prev.includes(s) ? prev : [...prev, s])}
                  onRemoveCustomState={(s) => setDestCustom((prev) => prev.filter((x) => x !== s))}
                  tone="teal"
                  anywhereLabel="Anywhere"
                />
              </div>
            </div>
            <div className="pt-2 border-t">
              <Label className="text-xs text-muted-foreground">Avoid going to</Label>
              <div className="mt-2">
                <RegionPills
                  selectedRegions={avoidRegions}
                  customStates={avoidCustom}
                  onToggleRegion={(k) => toggleRegion(k, avoidRegions, setAvoidRegions)}
                  onClearAll={() => { setAvoidRegions([]); setAvoidCustom([]); }}
                  onAddCustomState={(s) => setAvoidCustom((prev) => prev.includes(s) ? prev : [...prev, s])}
                  onRemoveCustomState={(s) => setAvoidCustom((prev) => prev.filter((x) => x !== s))}
                  tone="destructive"
                  anywhereLabel="None"
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={DollarSign} title="Financial Filters">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Min total rate ($)</Label>
                <Input
                  type="number" value={minRate}
                  onChange={(e) => setMinRate(e.target.value)}
                  placeholder="2500" className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Min RPM ($)</Label>
                <Input
                  type="number" step="0.01" value={minRpm}
                  onChange={(e) => setMinRpm(e.target.value)}
                  placeholder="2.50" className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Max weight (lbs)</Label>
                <Input
                  type="number" value={maxWeight}
                  onChange={(e) => setMaxWeight(e.target.value)}
                  placeholder="44000" className="mt-1"
                />
              </div>
            </div>
          </SectionCard>

          <div className="rounded-xl border bg-card p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label>Email me on matches</Label>
                <p className="text-xs text-muted-foreground">In-app alerts always on. SMS coming in Stage 2.</p>
              </div>
              <Switch checked={notifyEmail} onCheckedChange={setNotifyEmail} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-score matches</Label>
                <p className="text-xs text-muted-foreground">Spends 1 credit per match to attach an ATLAS score.</p>
              </div>
              <Switch checked={autoScore} onCheckedChange={setAutoScore} />
            </div>
          </div>
        </div>

        {submitError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {submitError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-brand-teal hover:bg-brand-teal/90 text-white">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {editing ? "Save changes" : "Start Watching"}
          </Button>
        </DialogFooter>

      </DialogContent>
    </Dialog>
  );
}
